import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  SelectChangeEvent,
  Table,
    TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  FormGroup,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  format, 
  addDays, 
  startOfWeek, 
  addWeeks, 
  subWeeks, 
  isSameDay, 
  parseISO 
} from 'date-fns';

// Services and types
import {
  Shift,
  WeeklySchedule,
  createShift,
  updateShift,
  deleteShift,
  getWeeklyShifts,
  calculateShiftDuration,
  calculateWorkingHours,
  formatTimeForDisplay,
  formatBreakInfo,
  getDailyTotals,
  createBulkShifts
} from '../../../firebase/services/shiftService';
import { getStaffMembers } from '../../../firebase/services/userService';
import { getActiveLocations, BusinessLocation } from '../../../firebase/services/locationService';
import { User } from '../../../models/types';

// Drag and drop types
interface DragItem {
  id: string;
  type: 'shift' | 'staff';
  shift?: Shift;
  staffId?: string;
}

const StaffSchedulingPage: React.FC = () => {
  // State management
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule | null>(null);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [locations, setLocations] = useState<BusinessLocation[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [shiftForm, setShiftForm] = useState({
    staffId: '',
    locationId: '',
    date: '',
    startTime: '',
    endTime: '',
    breakBeforeHours: '',
    breakDuration: '',
    breakMandatory: false,
    position: '',
    hourlyRate: '',
    notes: '',
    selectedDays: [] as string[] // For copying to multiple days
  });

  // Initialize data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load weekly schedule when week changes
  useEffect(() => {
    if (staffMembers.length > 0) {
      loadWeeklySchedule();
    }
  }, [currentWeek, staffMembers]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [staff, locationsList] = await Promise.all([
        getStaffMembers(),
        getActiveLocations()
      ]);
      
      setStaffMembers(staff);
      setLocations(locationsList);
      // Initialize with all staff members and locations selected
      setSelectedStaffIds(staff.map(s => s.id));
      setSelectedLocationIds(locationsList.map(l => l.id!));
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load staff and location data');
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklySchedule = async () => {
    try {
      const schedule = await getWeeklyShifts(currentWeek);
      setWeeklySchedule(schedule);
    } catch (err) {
      console.error('Error loading weekly schedule:', err);
      setError('Failed to load weekly schedule');
    }
  };

  // Week navigation
  const goToPreviousWeek = () => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  // Get days of the week
  const getWeekDays = useCallback(() => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday start
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [currentWeek]);

  // Drag and drop handlers
  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    // If dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    try {
      // Check if data is loaded
      if (!weeklySchedule || staffMembers.length === 0) {
        setError('Data not loaded yet. Please wait and try again.');
        return;
      }

      const shift = weeklySchedule.shifts.find(s => s.id === draggableId);
      
      if (!shift) {
        setError('Shift not found');
        return;
      }

      // Parse destination and source - handle UUIDs with hyphens properly
      let updatedShiftData: Partial<Shift> = {};

      if (destination.droppableId.startsWith('open-')) {
        // Moving to open shifts (unassign staff)
        const dateStr = destination.droppableId.replace('open-', '');
        
        // Only update date if moving to a different day
        if (shift.date !== dateStr) {
          updatedShiftData = {
            date: dateStr,
            staffId: '',
            staffName: ''
          };
        } else {
          // Same day, just unassign staff
          updatedShiftData = {
            staffId: '',
            staffName: ''
          };
        }
        
        setSuccess('Shift unassigned successfully');
      } else if (destination.droppableId.startsWith('staff-')) {
        // Moving to a staff member's slot (format: staff-{staffId}-{date})
        // Extract the date from the end (last 10 characters: YYYY-MM-DD)
        const droppableId = destination.droppableId;
        const dateStr = droppableId.slice(-10); // Get last 10 characters (YYYY-MM-DD)
        const staffPart = droppableId.slice(6, -11); // Remove 'staff-' prefix and '-YYYY-MM-DD' suffix
        const targetStaffId = staffPart;
        
        // Find the target staff member
        const targetStaff = staffMembers.find(s => s.id === targetStaffId);
        if (!targetStaff) {
          const availableIds = staffMembers.map(s => s.id);
          const message = `Staff member not found. Looking for: "${targetStaffId}", Available IDs: [${availableIds.join(', ')}]`;
          console.error(message);
          setError(message);
          return;
        }

        // Get staff name safely
        const staffName = targetStaff ? `${targetStaff.firstName} ${targetStaff.lastName}` : 'Unknown Staff';

        // Only update date if moving to a different day
        if (shift.date !== dateStr) {
          updatedShiftData = {
            date: dateStr,
            staffId: targetStaffId,
            staffName: staffName
          };
        } else {
          // Same day, just assign staff
          updatedShiftData = {
            staffId: targetStaffId,
            staffName: staffName
          };
        }
        
        // Check if this is assignment from open shifts
        if (source.droppableId.startsWith('open-')) {
          setSuccess(`Shift assigned to ${targetStaff.firstName} ${targetStaff.lastName}`);
        } else {
          setSuccess('Shift moved successfully');
        }
      } else {
        setError(`Invalid drop destination: ${destination.droppableId}`);
        return;
      }

      // Update the shift in the database
      await updateShift(shift.id!, updatedShiftData);
      
      // Reload the schedule
      await loadWeeklySchedule();
      
    } catch (err) {
      console.error('Error moving shift:', err);
      setError(`Failed to move shift: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Shift form handlers
  const openShiftDialog = (date?: string, staffId?: string) => {
    setShiftForm({
      staffId: staffId || '',
      locationId: locations[0]?.id || '',
      date: date || format(new Date(), 'yyyy-MM-dd'),
      startTime: '09:00',
      endTime: '17:00',
      breakBeforeHours: '',
      breakDuration: '',
      breakMandatory: false,
      position: '',
      hourlyRate: '',
      notes: '',
      selectedDays: date ? [date] : []
    });
    setEditingShift(null);
    setShiftDialogOpen(true);
  };

  const openEditShiftDialog = (shift: Shift) => {
    setShiftForm({
      staffId: shift.staffId || '',
      locationId: shift.locationId,
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      breakBeforeHours: shift.breakBeforeHours?.toString() || '',
      breakDuration: shift.breakDuration?.toString() || '',
      breakMandatory: shift.breakMandatory || false,
      position: shift.position || '',
      hourlyRate: shift.hourlyRate?.toString() || '',
      notes: shift.notes || '',
      selectedDays: [shift.date]
    });
    setEditingShift(shift);
    setShiftDialogOpen(true);
  };

  const handleFormChange = (field: string, value: string | string[] | boolean) => {
    setShiftForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDaySelection = (date: string, checked: boolean) => {
    setShiftForm(prev => ({
      ...prev,
      selectedDays: checked 
        ? [...prev.selectedDays, date]
        : prev.selectedDays.filter(d => d !== date)
    }));
  };

  const getWeekDaysForForm = () => {
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(currentWeekStart, i);
      return {
        date: format(day, 'yyyy-MM-dd'),
        label: format(day, 'EEEE'),
        shortLabel: format(day, 'EEE')
      };
    });
  };

  const handleSaveShift = async () => {
    try {
      const location = locations.find(l => l.id === shiftForm.locationId);
      
      if (!location) {
        setError('Please select a valid location');
        return;
      }

      // Validate break configuration if provided
      if ((shiftForm.breakBeforeHours && !shiftForm.breakDuration) || 
          (!shiftForm.breakBeforeHours && shiftForm.breakDuration)) {
        setError('Please provide both break timing and duration, or leave both empty');
        return;
      }

      const duration = calculateShiftDuration(shiftForm.startTime, shiftForm.endTime);
      
      // Handle staff assignment (can be empty for open shifts)
      let staffData = {};
      if (shiftForm.staffId) {
        const staff = staffMembers.find(s => s.id === shiftForm.staffId);
        if (!staff) {
          setError('Please select a valid staff member');
          return;
        }
        staffData = {
          staffId: shiftForm.staffId,
          staffName: `${staff.firstName} ${staff.lastName}`,
        };
      }
      
      const baseShiftData: any = {
        ...staffData,
        locationId: shiftForm.locationId,
        locationName: location.name,
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        duration,
        status: 'scheduled' as const,
        createdBy: 'current-user' // In real app, get from auth context
      };

      // Only add break fields if they have values
      if (shiftForm.breakBeforeHours && shiftForm.breakDuration) {
        baseShiftData.breakBeforeHours = parseFloat(shiftForm.breakBeforeHours);
        baseShiftData.breakDuration = parseInt(shiftForm.breakDuration);
        baseShiftData.breakMandatory = shiftForm.breakMandatory;
      }

      // Only add position if it has a value
      if (shiftForm.position) {
        baseShiftData.position = shiftForm.position;
      }

      // Only add hourly rate if it has a value
      if (shiftForm.hourlyRate) {
        baseShiftData.hourlyRate = parseFloat(shiftForm.hourlyRate);
      }

      // Only add notes if they have a value
      if (shiftForm.notes) {
        baseShiftData.notes = shiftForm.notes;
      }

      if (editingShift) {
        // When editing, only update the single shift
        await updateShift(editingShift.id!, { ...baseShiftData, date: shiftForm.date });
        setSuccess('Shift updated successfully');
      } else {
        // When creating, check if multiple days are selected
        const selectedDays = shiftForm.selectedDays.length > 0 ? shiftForm.selectedDays : [shiftForm.date];
        
        if (selectedDays.length === 1) {
          // Single shift
          await createShift({ ...baseShiftData, date: selectedDays[0] });
          setSuccess(shiftForm.staffId ? 'Shift created successfully' : 'Open shift created successfully');
        } else {
          // Multiple shifts
          const shiftsToCreate = selectedDays.map(date => ({
            ...baseShiftData,
            date
          }));
          
          await createBulkShifts(shiftsToCreate);
          setSuccess(`${selectedDays.length} shifts created successfully`);
        }
      }

      setShiftDialogOpen(false);
      await loadWeeklySchedule();
    } catch (err) {
      console.error('Error saving shift:', err);
      setError('Failed to save shift');
    }
  };

  const handleDeleteShift = async (shiftId: string) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      await deleteShift(shiftId);
      setSuccess('Shift deleted successfully');
      await loadWeeklySchedule();
    } catch (err) {
      console.error('Error deleting shift:', err);
      setError('Failed to delete shift');
    }
  };

  // Filter shifts by location
  const getFilteredShifts = (shifts: Shift[]) => {
    if (selectedLocationIds.length === locations.length) return shifts;
    return shifts.filter(shift => selectedLocationIds.includes(shift.locationId));
  };

  // Render shift card
  const renderShiftCard = (shift: Shift, index: number) => (
    <Draggable key={shift.id} draggableId={shift.id!} index={index}>
      {(provided) => (
                 <Card
           ref={provided.innerRef}
           {...provided.draggableProps}
           {...provided.dragHandleProps}
           onClick={(e) => e.stopPropagation()}
           sx={{
             mb: 1,
             backgroundColor: shift.staffId ? getShiftColor(shift.locationName) : '#ff5722',
             cursor: 'grab',
             '&:hover': {
               boxShadow: 2
             }
           }}
         >
                     <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2" fontWeight="bold" color="white">
                 {shift.staffId ? (shift.position || shift.locationName) : 'Open Shift'}
               </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditShiftDialog(shift);
                  }}
                  sx={{ color: 'white', p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteShift(shift.id!);
                  }}
                  sx={{ color: 'white', p: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
                         <Typography variant="caption" color="rgba(255,255,255,0.9)">
               {formatTimeForDisplay(shift.startTime)} - {formatTimeForDisplay(shift.endTime)}
               {shift.breakBeforeHours && shift.breakDuration && (
                 <Box component="span" sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.8 }}>
                   {formatBreakInfo(shift)}
                 </Box>
               )}
             </Typography>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );

  // Get color for shift based on location
  const getShiftColor = (locationName: string): string => {
    const colors: { [key: string]: string } = {
      'Marina': '#2196f3',
      'Store': '#4caf50',
      'Kitchen': '#ff9800',
      'Office': '#9c27b0',
      'Warehouse': '#607d8b'
    };
    
    const matchedColor = Object.keys(colors).find(key => 
      locationName.toLowerCase().includes(key.toLowerCase())
    );
    
    return matchedColor ? colors[matchedColor] : '#757575';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const weekDays = getWeekDays();
  const filteredShifts = weeklySchedule ? getFilteredShifts(weeklySchedule.shifts) : [];
  const visibleStaffMembers = staffMembers.filter(staff => selectedStaffIds.includes(staff.id));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Staff Scheduling
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openShiftDialog()}
        >
          Add Shift
        </Button>
      </Box>

      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filter by Location</InputLabel>
            <Select
              multiple
              value={selectedLocationIds}
              onChange={(e: SelectChangeEvent<typeof selectedLocationIds>) => {
                const value = typeof e.target.value === 'string' ? [e.target.value] : e.target.value;
                
                // Handle select/unselect all
                if (value.includes('SELECT_ALL')) {
                  if (selectedLocationIds.length === locations.length) {
                    setSelectedLocationIds([]);
                  } else {
                    setSelectedLocationIds(locations.map(l => l.id!));
                  }
                } else {
                  setSelectedLocationIds(value);
                }
              }}
              input={<OutlinedInput label="Filter by Location" />}
              renderValue={(selected) => 
                selected.length === locations.length 
                  ? 'All Locations' 
                  : `${selected.length} selected`
              }
            >
              {/* Select/Unselect All Option */}
              <MenuItem
                value="SELECT_ALL"
                sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Checkbox 
                  checked={selectedLocationIds.length === locations.length}
                  indeterminate={selectedLocationIds.length > 0 && selectedLocationIds.length < locations.length}
                />
                <ListItemText 
                  primary={
                    selectedLocationIds.length === locations.length 
                      ? 'Unselect All' 
                      : 'Select All'
                  } 
                />
              </MenuItem>
              
              {locations.map(location => (
                <MenuItem key={location.id} value={location.id}>
                  <Checkbox checked={selectedLocationIds.indexOf(location.id!) > -1} />
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ mr: 1, fontSize: 16 }} />
                        {location.name}
                      </Box>
                    } 
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Show Staff Members</InputLabel>
            <Select
              multiple
              value={selectedStaffIds}
              onChange={(e: SelectChangeEvent<typeof selectedStaffIds>) => {
                const value = typeof e.target.value === 'string' ? [e.target.value] : e.target.value;
                
                // Handle select/unselect all
                if (value.includes('SELECT_ALL')) {
                  if (selectedStaffIds.length === staffMembers.length) {
                    setSelectedStaffIds([]);
                  } else {
                    setSelectedStaffIds(staffMembers.map(s => s.id));
                  }
                } else {
                  setSelectedStaffIds(value);
                }
              }}
              input={<OutlinedInput label="Show Staff Members" />}
              renderValue={(selected) => 
                selected.length === staffMembers.length 
                  ? 'All Staff' 
                  : `${selected.length} selected`
              }
            >
              {/* Select/Unselect All Option */}
              <MenuItem
                value="SELECT_ALL"
                sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Checkbox 
                  checked={selectedStaffIds.length === staffMembers.length}
                  indeterminate={selectedStaffIds.length > 0 && selectedStaffIds.length < staffMembers.length}
                />
                <ListItemText 
                  primary={
                    selectedStaffIds.length === staffMembers.length 
                      ? 'Unselect All' 
                      : 'Select All'
                  } 
                />
              </MenuItem>
              
              {staffMembers.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  <Checkbox checked={selectedStaffIds.indexOf(staff.id) > -1} />
                  <ListItemText primary={`${staff.firstName} ${staff.lastName}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={goToPreviousWeek}>
              <ChevronLeftIcon />
            </IconButton>
            <Button variant="outlined" onClick={goToCurrentWeek}>
              This Week
            </Button>
            <IconButton onClick={goToNextWeek}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2 }}>
              {format(weekDays[0], 'MMM d')} - {format(weekDays[6], 'MMM d, yyyy')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <DragDropContext onDragEnd={onDragEnd}>
        <Grid container spacing={2}>
          {/* Schedule Grid */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Team member</TableCell>
                    {weekDays.map(day => (
                      <TableCell key={day.toString()} align="center" sx={{ minWidth: 150 }}>
                        <Typography variant="subtitle2">
                          {format(day, 'EEE M/d')}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Open Shifts Row */}
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Open Shifts
                        </Typography>
                      </Box>
                    </TableCell>
                    {weekDays.map(day => {
                      const dayStr = format(day, 'yyyy-MM-dd');
                      const dayOpenShifts = filteredShifts.filter(
                        shift => !shift.staffId && shift.date === dayStr
                      );
                      
                      return (
                        <TableCell key={dayStr} sx={{ p: 1, verticalAlign: 'top' }}>
                                                     <Droppable droppableId={`open-${dayStr}`}>
                             {(provided) => (
                               <Box
                                 ref={provided.innerRef}
                                 {...provided.droppableProps}
                                 onClick={dayOpenShifts.length === 0 ? () => openShiftDialog(dayStr) : undefined}
                                 sx={{
                                   minHeight: 60,
                                   backgroundColor: 'grey.100',
                                   borderRadius: 1,
                                   p: 0.5,
                                   border: '2px dashed',
                                   borderColor: 'grey.300',
                                   cursor: dayOpenShifts.length === 0 ? 'pointer' : 'default',
                                   position: 'relative',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   '&:hover': dayOpenShifts.length === 0 ? {
                                     backgroundColor: 'grey.200',
                                     '& .add-shift-icon': {
                                       opacity: 1
                                     }
                                   } : {}
                                 }}
                               >
                                                                  {dayOpenShifts.length === 0 && (
                                   <AddIcon 
                                     className="add-shift-icon"
                                     sx={{ 
                                       position: 'absolute',
                                       opacity: 0,
                                       color: 'primary.main',
                                       fontSize: 24,
                                       transition: 'opacity 0.2s',
                                       zIndex: 1,
                                       pointerEvents: 'none'
                                     }} 
                                   />
                                 )}
                                 {dayOpenShifts.map((shift, index) => 
                                   renderShiftCard(shift, index)
                                 )}
                                 {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {visibleStaffMembers.map(staff => (
                    <TableRow key={staff.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {staff.firstName} {staff.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ${staff.hourlyRate || 0}/hr
                          </Typography>
                        </Box>
                      </TableCell>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayShifts = filteredShifts.filter(
                          shift => shift.staffId === staff.id && shift.date === dayStr
                        );
                        
                        return (
                          <TableCell key={dayStr} sx={{ p: 1, verticalAlign: 'top' }}>
                            <Droppable droppableId={`staff-${staff.id}-${dayStr}`}>
                              {(provided) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  onClick={dayShifts.length === 0 ? () => openShiftDialog(dayStr, staff.id) : undefined}
                                  sx={{
                                    minHeight: 60,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    p: 0.5,
                                    cursor: dayShifts.length === 0 ? 'pointer' : 'default',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': dayShifts.length === 0 ? {
                                      backgroundColor: 'grey.100',
                                      '& .add-shift-icon': {
                                        opacity: 1
                                      }
                                    } : {}
                                  }}
                                >
                                                                     {dayShifts.length === 0 && (
                                     <AddIcon 
                                       className="add-shift-icon"
                                       sx={{ 
                                         position: 'absolute',
                                         opacity: 0,
                                         color: 'primary.main',
                                         fontSize: 24,
                                         transition: 'opacity 0.2s',
                                         zIndex: 1,
                                         pointerEvents: 'none'
                                       }} 
                                     />
                                   )}
                                   {dayShifts.map((shift, index) => 
                                     renderShiftCard(shift, index)
                                   )}
                                   {provided.placeholder}
                                </Box>
                              )}
                            </Droppable>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  
                  {/* Totals Row */}
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                    {weekDays.map(day => {
                      const dayStr = format(day, 'yyyy-MM-dd');
                      const totals = getDailyTotals(filteredShifts, dayStr);
                      
                      return (
                        <TableCell key={dayStr} align="center">
                          <Typography variant="body2" fontWeight="bold">
                            ${totals.cost.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {totals.hours.toFixed(1)}h
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DragDropContext>

      {/* Shift Dialog */}
      <Dialog open={shiftDialogOpen} onClose={() => setShiftDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingShift ? 'Edit Shift' : 'Create New Shift'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Staff Member (Optional)</InputLabel>
                <Select
                  value={shiftForm.staffId}
                  onChange={(e) => handleFormChange('staffId', e.target.value)}
                  label="Staff Member (Optional)"
                >
                  <MenuItem value="">
                    <em>No staff assigned (Open Shift)</em>
                  </MenuItem>
                  {staffMembers.map(staff => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.firstName} {staff.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={shiftForm.locationId}
                  onChange={(e) => handleFormChange('locationId', e.target.value)}
                  label="Location"
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Date and Time Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Shift Time</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={shiftForm.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={shiftForm.startTime}
                onChange={(e) => handleFormChange('startTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={shiftForm.endTime}
                onChange={(e) => handleFormChange('endTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Break Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Break Configuration (Optional)</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Take break before (hours)"
                type="number"
                value={shiftForm.breakBeforeHours}
                onChange={(e) => handleFormChange('breakBeforeHours', e.target.value)}
                placeholder="e.g., 4 (4 hours into shift)"
                inputProps={{ min: 0.5, max: 12, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Break duration (minutes)"
                type="number"
                value={shiftForm.breakDuration}
                onChange={(e) => handleFormChange('breakDuration', e.target.value)}
                placeholder="e.g., 30"
                inputProps={{ min: 15, max: 120, step: 15 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={shiftForm.breakMandatory}
                    onChange={(e) => handleFormChange('breakMandatory', e.target.checked)}
                  />
                }
                label="Mandatory Break"
                sx={{ mt: 2 }}
              />
            </Grid>

            {/* Copy to Multiple Days Section */}
            {!editingShift && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Copy to Multiple Days</Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormGroup row>
                    {getWeekDaysForForm().map(({ date, label, shortLabel }) => (
                      <FormControlLabel
                        key={date}
                        control={
                          <Checkbox
                            checked={shiftForm.selectedDays.includes(date)}
                            onChange={(e) => handleDaySelection(date, e.target.checked)}
                          />
                        }
                        label={shortLabel}
                        sx={{ minWidth: 100 }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              </>
            )}

            {/* Additional Details Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Additional Details</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position"
                value={shiftForm.position}
                onChange={(e) => handleFormChange('position', e.target.value)}
                placeholder="e.g., Marina Manager, Marina Staff"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hourly Rate"
                type="number"
                value={shiftForm.hourlyRate}
                onChange={(e) => handleFormChange('hourlyRate', e.target.value)}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={shiftForm.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveShift} variant="contained">
            {editingShift ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffSchedulingPage; 